import { GraphQLError } from "graphql"

import { CommerceService } from "@/services/commerce"

import { checkAdmAccessRightsToAdmNumber } from "./admPermissionsHelper"
import { RolePermissions, Roles } from "./permissionsConfig"
import { checkRegionalLeadAccessRightsToAdmNumber } from "./regionalLeadPermissionsHelper"

/**
 * Checks the permissions of a user to perform a specific operation on a domain object.
 * @param {Object} options - The options for checking permissions.
 * @param {Object} options.user - The user object.
 * @param {string} options.admNumber - Optional - an ADM number to check permissions to.
 * @param {string} options.domainObject - The domain object.
 * @param {string} options.operation - The operation to perform.
 * @returns {Promise<void>} - A promise that resolves if the user has permission, otherwise throws an error.
 * @throws {GraphQLError} - Throws a GraphQLError if the user does not have permission.
 */
export const checkPermissions = async ({ user, admNumber, domainObject, operation }) => {
  const selectedAdmNumber = admNumber || user?.loggedInAdm?.admNumber

  if (user.role === Roles.ADMIN || isPermissionCheckDisabled) {
    return // no checks
  }

  if (user.role === Roles.ADM) {
    await checkAdmAccessRightsToAdmNumber({ user, admNumber: selectedAdmNumber })
  } else if ([Roles.REGIONAL_LEAD, Roles.ADM_GVL].includes(user.role)) {
    await checkRegionalLeadAccessRightsToAdmNumber({ user, admNumber: selectedAdmNumber })
    if (isActingInAdmRole(user, selectedAdmNumber)) {
      return checkPermissionsToCommand({ role: [Roles.ADM], domainObject, operation })
    }
  }
  return checkPermissionsToCommand({ role: user.role, domainObject, operation })
}

/**
 * Retrieves assigned customers based on the provided ADM number.
 * @param {Object} options - The options for retrieving assigned customers.
 * @param {string} options.admNumber - The ADM number to filter the customers.
 * @param {number} [options.limit=20] - The maximum number of customers to retrieve per request.
 * @returns {Promise<Array>} - A promise that resolves to an array of assigned customers.
 */
export const getAssignedCustomers = async ({ admNumber, limit = 500 }) => {
  const commerceApi = CommerceService.api()
  let allCustomers = []

  let offset = 0
  let response

  do {
    response = await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `custom(fields(admSalesRepresentive="${admNumber}"))`,
          limit: limit,
          offset: offset,
        },
      })
      .execute()

    allCustomers = allCustomers.concat(response?.body.results)
    offset += limit
  } while (response?.body.total > allCustomers.length)

  return allCustomers
}

/**
 * Checks if the user has permission to access a specific customer.
 * @param {Object} options - The options for checking permission.
 * @param {string} options.user - The logged in user.
 * @param {string} options.customerNumber - The customer number.
 * @returns {Promise<boolean>} - A promise that resolves to true if the user has permission, false otherwise.
 * @throws {GraphQLError} - Throws a GraphQLError if the user does not have permission.
 */
export const validateAccessRightsToCustomer = async ({ user, customerNumber }) => {
  if (user.role === Roles.ADMIN || isIdmUser(user.role) || user?.allowAllCustomerSearch || isPermissionCheckDisabled) {
    return
  }

  const commerceApi = CommerceService.api()
  const response = await commerceApi
    .customers()
    .get({
      queryArgs: {
        where: `customerNumber="${customerNumber}" and custom(fields(admSalesRepresentive="${user.loggedInAdm.admNumber}"))`,
      },
    })
    .execute()

  if (response?.body.total === 0) {
    throw new GraphQLError("You are not authorized to perform this operation.", {
      extensions: {
        code: "FORBIDDEN",
      },
    })
  }
}

export const isPermissionCheckDisabled =
  process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_ADM_APP_PERMISSIONS_CHECK_DISABLED === "true"

const checkPermissionsToCommand = ({ role, domainObject, operation }) => {
  if (!RolePermissions[domainObject][operation][role]) {
    throw new GraphQLError("You are not authorized to perform this operation.", {
      extensions: {
        code: "FORBIDDEN",
      },
    })
  }
}

function isActingInAdmRole(user, selectedAdmNumber) {
  return user.admNumber === selectedAdmNumber
}

/**
 * Checks if logged in users role is IDM
 *
 * @param {object} user
 */
export const isOnlyIdmAction = (user) => {
  if (!user || !user?.role) {
    throw new GraphQLError("User data is missing or user is not logged in!", {
      extensions: {
        code: "FORBIDDEN",
      },
    })
  }

  if (isIdmUser(user?.role) || user?.allowAllCustomerSearch) return

  throw new GraphQLError("You are not authorized to perform this operation.", {
    extensions: {
      code: "FORBIDDEN",
    },
  })
}

/**
 * Checks if logged in users role is a IDM
 *
 * @param {boolean}
 */
export const isIdmUser = (role) => {
  const idmRoles = [
    "IDM_USER",
    "IDM_DISPONENT",
    "IDM_LEAD",
    "IDM_THEKE",
    "IDM_SPRINGER",
    "IDM_SPRINGER_LEAD",
    "IDM_PROCUREMENT",
  ]
  return idmRoles.includes(role)
}

/**
 * Checks if logged in users role is a ADM lead or admin
 *
 * @param {boolean}
 */
export const isAdmLeadUser = (role) => {
  const admRoles = ["ADMIN", "REGIONAL_LEAD", "ADM_GVL"]
  return admRoles.includes(role)
}

/**
 * Checks if logged in users role is a lead or admin
 *
 * @param {boolean}
 */
export const isLeadAdminUser = (role) => {
  const leadRoles = ["ADMIN", "REGIONAL_LEAD", "IDM_LEAD", "IDM_SPRINGER_LEAD"]
  return leadRoles.includes(role)
}

/**
 * Checks if logged in users role can access purchase prices
 *
 * @param {string} role
 * @returns {boolean}
 */
export const canAccessPurchasePrice = (role) => {
  const purchasePriceRoles = [
    "ADMIN",
    "REGIONAL_LEAD",
    "IDM_LEAD",
    "IDM_SPRINGER_LEAD",
    "IDM_DISPONENT",
    "IDM_PROCUREMENT",
  ]
  return purchasePriceRoles.includes(role)
}
